import babbel from "../config/babbel.js";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import callAPI from "../api/index.js";
import InputMask from 'react-input-mask';
import { isValidPhoneNumber } from 'react-phone-number-input';

const COUNTRY_OPTIONS = [
    { code: 'bg', label: 'Bulgaria (+359)', dialCode: '+359', phoneMask: '9 999 99999', service: 'babbel', lang: 'bg' },
    { code: 'my', label: 'Malaysia (+60)', dialCode: '+60', phoneMask: '9 999 99999', service: 'babbel', lang: 'ms' },
    { code: 'ca', label: 'Canada (+1)', dialCode: '+1', phoneMask: '999 999 9999', service: 'babbel', lang: 'en' },
    { code: 'sg', label: 'Singapore (+65)', dialCode: '+65', phoneMask: '9999 9999', service: 'babbel', lang: 'en' },
    { code: 'se', label: 'Sweden (+46)', dialCode: '+46', phoneMask: '99 999 9999', service: 'babbel', lang: 'sv' },
];

// Messages by language
const MESSAGES = {
    en: {
        ERROR: 'Something went wrong. Please try again.',
        ALREADY_SUBSCRIBED: 'You already have an active subscription for this service.',
        INVALID_MSISDN: 'The phone number you entered is invalid.',
        FALLBACK: 'Unsubscribe failed. Please try again.',
        SUCCESS: 'You have successfully unsubscribed from the service.'
    },
    bg: {
        ERROR: 'Нещо се обърка. Моля, опитайте отново.',
        ALREADY_SUBSCRIBED: 'Вече имате активен абонамент за тази услуга.',
        INVALID_MSISDN: 'Въведеният телефонен номер е невалиден.',
        FALLBACK: 'Неуспешен отказ от абонамент. Моля, опитайте отново.',
        SUCCESS: 'Успешно сте отписани от услугата.'
    },
    ms: {
        ERROR: 'Sesuatu telah berlaku. Sila cuba lagi.',
        ALREADY_SUBSCRIBED: 'Anda sudah mempunyai langganan aktif untuk perkhidmatan ini.',
        INVALID_MSISDN: 'Nombor telefon yang anda masukkan tidak sah.',
        FALLBACK: 'Nyahlanggan gagal. Sila cuba lagi.',
        SUCCESS: 'Anda telah berjaya menyahlanggan perkhidmatan tersebut.'
    },
    fr: {
        ERROR: 'Quelque chose s\'est mal passé. Veuillez réessayer.',
        ALREADY_SUBSCRIBED: 'Vous avez déjà un abonnement actif pour ce service.',
        INVALID_MSISDN: 'Le numéro de téléphone que vous avez entré est invalide.',
        FALLBACK: 'La désinscription a échoué. Veuillez réessayer.',
        SUCCESS: 'Vous vous êtes désinscrit avec succès du service.'
    },
    sv: {
        ERROR: 'Något gick fel. Vänligen försök igen.',
        ALREADY_SUBSCRIBED: 'Du har redan en aktiv prenumeration för denna tjänst.',
        INVALID_MSISDN: 'Telefonnumret du angav är ogiltigt.',
        FALLBACK: 'Avprenumeration misslyckades. Vänligen försök igen.',
        SUCCESS: 'Du har framgångsrikt avprenumererat från tjänsten.'
    }
};

const translations = {
    en: {
        needToUnsub: 'Need to unsubscribe?',
        unsubscribe: 'Unsubscribe',
        country: 'Country',
        phoneNumber: 'Phone Number',
        processing: 'Processing...',
        cancel: 'Cancel',
        confirmMessage: 'Unsubscribe from this service?',
        invalidPhone: 'Please enter a valid phone number'
    },
    fr: {
        needToUnsub: 'Besoin de vous désabonner?',
        unsubscribe: 'Se désabonner',
        country: 'Pays',
        phoneNumber: 'Numéro de téléphone',
        processing: 'Traitement...',
        cancel: 'Annuler',
        confirmMessage: 'Se désabonner de ce service?',
        invalidPhone: 'Veuillez entrer un numéro de téléphone valide'
    },
    bg: {
        needToUnsub: 'Трябва ли да се отпишете?',
        unsubscribe: 'Отписване',
        country: 'Държава',
        phoneNumber: 'Телефонен номер',
        processing: 'Обработка...',
        cancel: 'Отказ',
        confirmMessage: 'Отписване от тази услуга?',
        invalidPhone: 'Моля, въведете валиден телефонен номер'
    },
    sv: {
        needToUnsub: 'Behöver du avprenumerera?',
        unsubscribe: 'Avprenumerera',
        country: 'Land',
        phoneNumber: 'Telefonnummer',
        processing: 'Bearbetar...',
        cancel: 'Avbryt',
        confirmMessage: 'Avprenumerera från denna tjänst?',
        invalidPhone: 'Vänligen ange ett giltigt telefonnummer'
    }
};

const languageNames = {
    en: "EN",
    fr: "FR",
    bg: "BG",
    sv: "SV"
};

const getMessage = (key, lang = 'en') => {
    const messages = MESSAGES[lang] || MESSAGES.en;
    return messages[key] || messages.FALLBACK;
};

const getLocaleCandidates = () => {
    const primary = navigator.language || navigator.userLanguage || '';
    const list = (navigator.languages && navigator.languages.length) ? navigator.languages : [];
    return [primary, ...list].filter(Boolean);
};

const detectDeviceLanguage = () => {
    const supported = Object.keys(translations);
    for (const tag of getLocaleCandidates()) {
        const code = tag.toLowerCase().split('-')[0];
        if (supported.includes(code)) return code;
    }
    return 'en';
};

const detectDeviceCountry = () => {
    for (const tag of getLocaleCandidates()) {
        if (!tag.includes('-')) continue;
        const region = tag.toLowerCase().split('-')[1];
        const match = COUNTRY_OPTIONS.find(c => c.code === region);
        if (match) return match;
    }
    return COUNTRY_OPTIONS.find(c => c.code === 'bg') || COUNTRY_OPTIONS[0];
};

export default function BabbelUnsub(_) {
    const config = babbel;
    const location = useLocation();

    // Language state
    const [language, setLanguage] = useState(location.state?.language || detectDeviceLanguage());
    const [showLanguageMenu, setShowLanguageMenu] = useState(false);
    const t = translations[language];

    // Unsub form state
    const [showUnsubForm, setShowUnsubForm] = useState(true);
    const [selectedCountry, setSelectedCountry] = useState(detectDeviceCountry());
    const [phoneNumber, setPhoneNumber] = useState('');
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [unsubLoading, setUnsubLoading] = useState(false);
    const [unsubSuccess, setUnsubSuccess] = useState(false);
    const [unsubError, setUnsubError] = useState('');

    const getFullPhoneNumber = () => {
        const cleanPhone = phoneNumber.replace(/[^0-9]/g, '');
        return selectedCountry.dialCode + cleanPhone;
    };

    const phoneIsValid = phoneNumber && isValidPhoneNumber(getFullPhoneNumber());

    const handleUnsubClick = () => {
        const fullPhone = getFullPhoneNumber();

        if (!isValidPhoneNumber(fullPhone)) {
            setUnsubError(t.invalidPhone);
            return;
        }
        setUnsubError('');
        setShowConfirmDialog(true);
    };

    const handleConfirmUnsub = async () => {
        setShowConfirmDialog(false);
        setUnsubLoading(true);
        setUnsubError('');

        const fullPhoneNumber = getFullPhoneNumber();

        try {
            const res = await callAPI({
                msisdn: fullPhoneNumber,
                country_code: selectedCountry.code,
                service_code: selectedCountry.service,
            }, 'unsubscribe');

            if (res?.status === 'success') {
                setUnsubSuccess(true);
                setPhoneNumber('');
            } else {
                setUnsubError(getMessage(res?.payload?.user_message ?? 'ERROR', language));
            }
        } catch (error) {
            setUnsubError(getMessage('ERROR', language));
        } finally {
            setUnsubLoading(false);
        }
    };

    return (
        <>
            <div
                className="babbel h-screen w-screen flex sm:flex-row overflow-hidden w-100"
                style={{
                    backgroundColor: config.background_color,
                    color: config.text_color,
                    fontFamily: config.font_family,
                }}
            >
                <div className="md:w-1/2 bg-gray-100 justify-center items-center relative hidden sm:flex w-1/4 flex-shrink-0">
                    <img
                        src={config.featured_image}
                        className="max-w-full h-auto w-[80%]"
                        alt="Featured"
                    />
                </div>
                <div className="w-full sm:w-3/4 md:w-1/2 flex flex-col bg-white h-screen">
                    {/* Header */}
                    <div className="w-full py-3 px-4 flex-shrink-0">
                        <div className="flex items-center justify-between w-full">
                            <div className="w-1/3"></div>
                            <div className="w-1/3 flex justify-center items-center">
                                {config.logo_url && !config.logo_under_button && (
                                    <img
                                        src={config.logo_url}
                                        alt={"Logo"}
                                        id="Logo"
                                        className={`${config.logo_size === "Small"
                                            ? "w-[300px] lg:w-[200px] md:w-[200px] max-w-none"
                                            : ""
                                        }`}
                                    />
                                )}
                            </div>
                            <div className="w-1/3 flex justify-end items-center relative">
                                <button
                                    onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                                    className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-sm md:text-base"
                                >
                                    <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                    </svg>
                                    <span className="font-semibold">{languageNames[language]}</span>
                                    <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>

                                {showLanguageMenu && (
                                    <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 min-w-[100px]">
                                        {Object.entries(languageNames).map(([code, name]) => (
                                            <button
                                                key={code}
                                                onClick={() => {
                                                    setLanguage(code);
                                                    setShowLanguageMenu(false);
                                                }}
                                                className={`w-full text-center px-4 py-2 hover:bg-gray-100 transition-colors font-semibold ${
                                                    language === code ? 'bg-orange-50 text-orange-600' : ''
                                                }`}
                                            >
                                                {name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 flex items-center justify-center px-4 overflow-y-auto">
                        <div className="w-full max-w-md">
                            {!showUnsubForm ? (
                                <button
                                    onClick={() => setShowUnsubForm(true)}
                                    className="text-gray-500 text-sm underline hover:text-gray-700"
                                >
                                    {t.needToUnsub}
                                </button>
                            ) : unsubSuccess ? (
                                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                                    {getMessage('SUCCESS', language)}
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-800">{t.unsubscribe}</h3>

                                    {/* Country Dropdown */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {t.country}
                                        </label>
                                        <select
                                            value={selectedCountry.code}
                                            onChange={(e) => {
                                                const country = COUNTRY_OPTIONS.find(c => c.code === e.target.value);
                                                setSelectedCountry(country);
                                                setPhoneNumber('');
                                            }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                                        >
                                            {COUNTRY_OPTIONS.map((country) => (
                                                <option key={country.code} value={country.code}>
                                                    {country.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Phone Number Input */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {t.phoneNumber}
                                        </label>
                                        <div className={`flex rounded-lg overflow-hidden border-2 transition-colors ${
                                            phoneIsValid
                                                ? 'border-green-500'
                                                : unsubError
                                                    ? 'border-red-500'
                                                    : 'border-gray-300'
                                        }`}>
                      <span className={`inline-flex items-center px-3 font-mono text-sm ${
                          phoneIsValid
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                      }`}>
                        {selectedCountry.dialCode}
                      </span>
                                            <InputMask
                                                mask={selectedCountry.phoneMask}
                                                value={phoneNumber}
                                                onChange={(e) => {
                                                    setPhoneNumber(e.target.value);
                                                    setUnsubError('');
                                                }}
                                                placeholder={selectedCountry.phoneMask.replace(/9/g, '_')}
                                                className={`flex-1 px-3 py-2 focus:outline-none font-mono tracking-wider ${
                                                    phoneIsValid ? 'bg-green-50' : 'bg-white'
                                                }`}
                                            />
                                            {phoneIsValid && (
                                                <span className="inline-flex items-center px-3 bg-green-100 text-green-600">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Error Message */}
                                    {unsubError && (
                                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded text-sm">
                                            {unsubError}
                                        </div>
                                    )}

                                    {/* Unsub Button */}
                                    <button
                                        onClick={handleUnsubClick}
                                        disabled={unsubLoading || !phoneIsValid}
                                        className={`w-full py-2 px-4 text-white rounded-lg transition-colors ${
                                            phoneIsValid && !unsubLoading
                                                ? 'bg-red-600 hover:bg-red-700 cursor-pointer'
                                                : 'bg-gray-400 cursor-not-allowed'
                                        }`}
                                    >
                                        {unsubLoading ? t.processing : t.unsubscribe}
                                    </button>

                                    <button
                                        onClick={() => window.history.back()}
                                        className="w-full text-gray-500 text-sm underline"
                                    >
                                        {t.cancel}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="w-full bg-black p-2 flex-shrink-0">
                        <div className="flex justify-center items-center">
                            {config.show_footer && (
                                <div
                                    className="flex gap-2 items-center"
                                    dangerouslySetInnerHTML={{
                                        __html: config.footer_content || "",
                                    }}
                                ></div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-5 max-w-xs w-full shadow-lg">
                        <p className="text-center text-gray-700 mb-5">
                            {t.confirmMessage}
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowConfirmDialog(false)}
                                className="flex-1 py-2 text-sm text-gray-600 hover:text-gray-800"
                            >
                                {t.cancel}
                            </button>
                            <button
                                onClick={handleConfirmUnsub}
                                className="flex-1 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                                {t.unsubscribe}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
